<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanAPI from 'dashboard/api/kanban';
import ContactKanbanPipelineBlock from 'dashboard/components-next/Contacts/ContactsSidebar/ContactKanbanPipelineBlock.vue';
import KanbanOpportunityModal from 'dashboard/modules/kanban/KanbanOpportunityModal.vue';
import LossReasonModal from 'dashboard/modules/kanban/LossReasonModal.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const { t } = useI18n();

const cards = ref([]);
const isLoading = ref(false);
const selectedCard = ref(null);
const pendingStatusChange = ref(null);
const pendingWaitingChange = ref(null);

const STATUS_ORDER = { open: 0, won: 1, lost: 2 };
const sortedCards = computed(() =>
  [...cards.value].sort(
    (a, b) => (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3)
  )
);

const pendingCard = computed(() =>
  pendingStatusChange.value
    ? cards.value.find(c => c.id === pendingStatusChange.value.cardId)
    : null
);
const isPendingPostSales = computed(
  () => pendingCard.value?.pipeline?.category === 'post_sales'
);
const lossReasons = computed(() => {
  if (!pendingCard.value?.pipeline) return [];
  return isPendingPostSales.value
    ? (pendingCard.value.pipeline.cancel_reasons ?? [])
    : (pendingCard.value.pipeline.loss_reasons ?? []);
});

const waitingReasons = computed(() => {
  if (!pendingWaitingChange.value) return [];
  const card = cards.value.find(
    c => c.id === pendingWaitingChange.value.cardId
  );
  return card?.pipeline?.waiting_reasons ?? [];
});

async function fetchCards() {
  if (!props.contactId) return;
  isLoading.value = true;
  try {
    const { data } = await KanbanAPI.getCardsByContact(props.contactId);
    cards.value = data.data ?? [];
  } catch {
    cards.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function moveCard(cardId, columnId, position, reason = null) {
  const card = cards.value.find(c => c.id === cardId);
  const postSales = card?.pipeline?.category === 'post_sales';
  try {
    await KanbanAPI.moveCard(cardId, columnId, position, {
      lossReason: postSales ? null : reason,
      cancelReason: postSales ? reason : null,
    });
    await fetchCards();
  } catch {
    // silently fail
  }
}

function onStatusChange({ card, targetStatus }) {
  if (targetStatus === 'lost') {
    const lostColumn = (card.columns ?? []).find(c => c.column_type === 'lost');
    if (!lostColumn) return;
    pendingStatusChange.value = {
      cardId: card.id,
      columnId: lostColumn.id,
      position: card.position,
    };
    return;
  }
  if (targetStatus === 'won') {
    const wonColumn = (card.columns ?? []).find(c => c.column_type === 'won');
    if (!wonColumn) return;
    moveCard(card.id, wonColumn.id, card.position);
    return;
  }
  if (targetStatus === 'waiting') {
    pendingWaitingChange.value = { cardId: card.id };
    return;
  }
  const defaultColumn = (card.columns ?? [])
    .filter(c => c.column_type === 'default')
    .sort((a, b) => a.position - b.position)[0];
  if (!defaultColumn) return;
  moveCard(card.id, defaultColumn.id, 0);
}

function onColumnChange({ card, columnId }) {
  const col = (card.columns ?? []).find(c => c.id === columnId);
  if (!col) return;
  if (col.column_type === 'lost') {
    pendingStatusChange.value = {
      cardId: card.id,
      columnId: col.id,
      position: card.position,
    };
    return;
  }
  moveCard(card.id, col.id, card.position);
}

async function addLossReason(reason) {
  const card = cards.value.find(
    c => c.id === pendingStatusChange.value?.cardId
  );
  if (!card?.pipeline) return;
  if (card.pipeline.category === 'post_sales') {
    const updated = [
      ...new Set([...(card.pipeline.cancel_reasons ?? []), reason]),
    ];
    await KanbanAPI.updatePipeline(card.pipeline.id, {
      cancel_reasons: updated,
    });
    card.pipeline.cancel_reasons = updated;
  } else {
    const updated = [...new Set([...card.pipeline.loss_reasons, reason])];
    await KanbanAPI.updatePipeline(card.pipeline.id, {
      loss_reasons: updated,
    });
    card.pipeline.loss_reasons = updated;
  }
}

async function confirmLoss(lossReason) {
  if (!pendingStatusChange.value) return;
  const { cardId, columnId, position } = pendingStatusChange.value;
  pendingStatusChange.value = null;
  await moveCard(cardId, columnId, position, lossReason);
}

function cancelLoss() {
  pendingStatusChange.value = null;
}

async function addWaitingReason(reason) {
  const card = cards.value.find(
    c => c.id === pendingWaitingChange.value?.cardId
  );
  if (!card?.pipeline) return;
  const updated = [
    ...new Set([...(card.pipeline.waiting_reasons ?? []), reason]),
  ];
  await KanbanAPI.updatePipeline(card.pipeline.id, {
    waiting_reasons: updated,
  });
  card.pipeline.waiting_reasons = updated;
}

async function confirmWaiting(reason) {
  if (!pendingWaitingChange.value) return;
  const { cardId } = pendingWaitingChange.value;
  pendingWaitingChange.value = null;
  try {
    await KanbanAPI.updateCard(cardId, {
      status: 'waiting',
      waiting_reason: reason,
    });
    await fetchCards();
  } catch {
    // silently fail
  }
}

function cancelWaiting() {
  pendingWaitingChange.value = null;
}

function onCardClick(card) {
  selectedCard.value = card;
}

function onModalSaved() {
  selectedCard.value = null;
  fetchCards();
}

function onModalDeleted() {
  selectedCard.value = null;
  fetchCards();
}

onMounted(fetchCards);
watch(() => props.contactId, fetchCards);
</script>

<template>
  <div>
    <div v-if="isLoading" class="flex justify-center py-3">
      <span class="text-xs text-slate-400"
        >{{ t('KANBAN.CONTACT_SECTION.TITLE') }}...</span
      >
    </div>

    <div
      v-else-if="sortedCards.length === 0"
      class="text-xs text-slate-400 py-2 px-1"
    >
      {{ t('KANBAN.OPPORTUNITIES_SECTION.EMPTY') }}
    </div>

    <div v-else class="flex flex-col">
      <ContactKanbanPipelineBlock
        v-for="card in sortedCards"
        :key="card.id"
        :card="card"
        @status-change="onStatusChange"
        @column-change="onColumnChange"
        @card-click="onCardClick"
      />
    </div>

    <LossReasonModal
      v-if="pendingStatusChange"
      :reasons="lossReasons"
      @confirm="confirmLoss"
      @cancel="cancelLoss"
      @create="addLossReason"
    />

    <LossReasonModal
      v-if="pendingWaitingChange"
      color="amber"
      :reasons="waitingReasons"
      @confirm="confirmWaiting"
      @cancel="cancelWaiting"
      @create="addWaitingReason"
    />

    <KanbanOpportunityModal
      v-if="selectedCard"
      :card="selectedCard"
      @close="selectedCard = null"
      @saved="onModalSaved"
      @deleted="onModalDeleted"
    />
  </div>
</template>
